import React, { useState } from 'react';

import { ThumbsDownIcon, ThumbsUpIcon } from '@phosphor-icons/react';
import { Check } from 'lucide-react';

import {
	Button,
	Modal,
	ModalBody,
	ModalFooter,
	Textarea,
	Typography,
} from './common';

interface FeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (
		ratingType: 'THUMBS_UP' | 'THUMBS_DOWN',
		reasonCode?: string,
		note?: string,
	) => void;
	ratingType?: 'THUMBS_UP' | 'THUMBS_DOWN';
	showCommentModal?: boolean;
}

const REASON_CODES = {
	NOT_ACCURATE: 'NOT_ACCURATE',
	CALCULATION_ERROR: 'CALCULATION_ERROR',
	MISSING_INFORMATION: 'MISSING_INFORMATION',
	OTHER: 'OTHER',
} as const;

type ReasonCode = (typeof REASON_CODES)[keyof typeof REASON_CODES];

const FeedbackModal: React.FC<FeedbackModalProps> = ({
	isOpen,
	onClose,
	onSubmit,
	ratingType,
	showCommentModal = false,
}) => {
	const [selectedReasonCode, setSelectedReasonCode] =
		useState<ReasonCode | null>(null);
	const [note, setNote] = useState('');

	if (!isOpen) return null;

	const handleSubmit = () => {
		onSubmit(ratingType!, selectedReasonCode || undefined, note);
		setSelectedReasonCode(null);
		setNote('');
	};

	const handleCancel = () => {
		setSelectedReasonCode(null);
		setNote('');
		onClose();
	};

	const handleReasonCodeSelect = (reasonCode: ReasonCode) => {
		setSelectedReasonCode(reasonCode);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			size="md"
			title="Submit Feedback"
		>
			<ModalBody>
				{!showCommentModal ? (
					// First modal - rating selection
					<div className="text-center space-y-6">
						<div className="mb-6">
							{ratingType === 'THUMBS_UP' ? (
								<ThumbsUpIcon
									weight="fill"
									className="w-12 h-12 text-primary-500 mx-auto"
								/>
							) : (
								<ThumbsDownIcon
									weight="fill"
									className="w-12 h-12 text-primary-500 mx-auto"
								/>
							)}
						</div>
						<Typography variant="h4" className="mb-4">
							Thank you for submitting your feedback.
						</Typography>
						<Typography variant="body2" color="muted" className="mb-6">
							{ratingType === 'THUMBS_UP'
								? 'What did you like about this step? (Optional)'
								: 'What was the issue with this data? How could it be improved?'}
						</Typography>
						<Textarea
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Enter your feedback description"
							className="h-24"
							fullWidth
						/>
					</div>
				) : (
					// Second modal - reason code selection (only for thumbs down) or notes (for thumbs up)
					<div className="text-center space-y-6">
						<div className="mb-6">
							{ratingType === 'THUMBS_UP' ? (
								<ThumbsUpIcon
									weight="fill"
									className="w-12 h-12 text-primary-500 mx-auto"
								/>
							) : (
								<ThumbsDownIcon
									weight="fill"
									className="w-12 h-12 text-primary-500 mx-auto"
								/>
							)}
						</div>
						<Typography variant="h4" className="mb-4">
							Thank you for submitting your feedback.
						</Typography>
						<Typography variant="body2" color="muted" className="mb-6">
							{ratingType === 'THUMBS_UP'
								? 'What did you like about this step? (Optional)'
								: 'What was the issue with this data? How could it be improved?'}
						</Typography>

						{/* Reason Code Selection - Only for thumbs down */}
						{ratingType === 'THUMBS_DOWN' && (
							<div className="mb-6">
								<div className="flex flex-wrap gap-2 justify-center">
									{Object.entries(REASON_CODES).map(([key, value]) => (
										<Button
											key={key}
											onClick={() => handleReasonCodeSelect(value)}
											variant={
												selectedReasonCode === value ? 'primary' : 'outline'
											}
											icon={selectedReasonCode === value ? Check : undefined}
											iconPosition="right"
											size="sm"
										>
											<span className="text-sm whitespace-nowrap">
												{key === 'NOT_ACCURATE' && 'Not accurate'}
												{key === 'CALCULATION_ERROR' && 'Calculation error'}
												{key === 'MISSING_INFORMATION' && 'Missing information'}
												{key === 'OTHER' && 'Other'}
											</span>
										</Button>
									))}
								</div>
							</div>
						)}

						{/* Additional Notes */}
						<Textarea
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Enter additional details (optional)"
							className="h-20"
							fullWidth
						/>
					</div>
				)}
			</ModalBody>
			<ModalFooter>
				<Button onClick={handleCancel} variant="outline" fullWidth>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					disabled={ratingType === 'THUMBS_DOWN' && !selectedReasonCode}
					variant="primary"
					fullWidth
				>
					Submit
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default FeedbackModal;
